"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SIGNATURE_REASON = void 0;
exports.isInitialsFieldType = isInitialsFieldType;
exports.resolveSignatureMeaning = resolveSignatureMeaning;
exports.signatureNameAndAttestation = signatureNameAndAttestation;
exports.signatureManifestationLines = signatureManifestationLines;
exports.composeSignatureAttestation = composeSignatureAttestation;
/** Reason written on every digital signature. Surfaces must not invent a different string. */
exports.SIGNATURE_REASON = 'accept';
/** Bundle field `type` for initials widgets. Meaning joins the attestation line. */
function isInitialsFieldType(type) {
    return (type || '').toUpperCase() === 'INITIALS';
}
/**
 * Display string for a stored signature reason.
 *
 * The stored value is a stable token, not prose - it is written server-side and
 * must stay comparable across locales - so a manifestation resolves it through
 * the locale instead of printing it. Without this a translated consent carries
 * a bare English keyword on its legal record.
 *
 * `localizedReason` must already be resolved, and undefined when the locale has
 * no entry; the token is then rendered unchanged, which is truthful rather than
 * showing a lookup key. An unrecognised stored token also passes through, so a
 * reason added later still renders something.
 */
function resolveSignatureMeaning(storedReason, localizedReason) {
    if (!storedReason) {
        return undefined;
    }
    if (storedReason !== exports.SIGNATURE_REASON) {
        return storedReason;
    }
    return localizedReason || storedReason;
}
/**
 * Ordered signature manifestation lines for the live bundle view and both
 * PDF bakers, matching Figma `Fields` `State=Filled`:
 *
 *     {signedBy} {attestation}
 *     {date} ({id})
 *
 * Live overlays bold `{signedBy}` on the first line. Date and id share one
 * line so the join is decided once. Callers must not append the id themselves.
 * The id is never truncated.
 *
 * The stored reason token is not its own line: the attestation already carries
 * the signing action ("accepts with …"), and a separate `accept` line is being
 * removed from the FRS. `meaning` / `compactMeaning` stay on the input type so
 * existing callers compile; they do not change the stamp.
 *
 * Line composition lives here, not in the callers. Three renderers build this
 * stamp (Studio baker, live bundle, patient app) and it is a Part 11 legal
 * record; an ordering decided three times is three chances to render the same
 * signature three different ways.
 *
 * No part carries a `Signed by ` / `Date: ` / `ID: ` prefix. Authored
 * signature-box geometry assumes that width.
 *
 * Fitting the result inside the box is the renderer's job - see
 * `fitOverlayFontSize` in both bakers. Every line here is an audit field, so
 * a renderer must scale the stamp down rather than drop a line.
 */
function dateAndIdLine(date, id) {
    const trimmedDate = date === null || date === void 0 ? void 0 : date.trim();
    const trimmedId = id === null || id === void 0 ? void 0 : id.trim();
    if (trimmedDate && trimmedId) {
        return `${trimmedDate} (${trimmedId})`;
    }
    return trimmedDate || trimmedId || undefined;
}
/**
 * When the printed name already ends with the role that starts the attestation
 * ("Rahul Participant" + "Participant accepts…"), joining them would print the
 * role twice. Drop the overlapping leading words from the attestation.
 */
function dropOverlappingAttestationPrefix(name, attestation) {
    const nameWords = name.split(/\s+/).filter(Boolean);
    const attWords = attestation.split(/\s+/).filter(Boolean);
    const max = Math.min(nameWords.length, attWords.length);
    for (let n = max; n >= 1; n -= 1) {
        const prefix = attWords.slice(0, n).join(' ');
        const suffix = nameWords.slice(-n).join(' ');
        if (suffix.toLowerCase() === prefix.toLowerCase()) {
            return attWords.slice(n).join(' ');
        }
    }
    return attestation;
}
function signatureNameAndAttestation(signedBy, attestation) {
    const name = signedBy.trim();
    const trimmed = attestation === null || attestation === void 0 ? void 0 : attestation.trim();
    if (!trimmed) {
        return name;
    }
    const rest = dropOverlappingAttestationPrefix(name, trimmed);
    return rest ? `${name} ${rest}` : name;
}
function signatureManifestationLines(input) {
    const lines = [signatureNameAndAttestation(input.signedBy, input.attestation)];
    const footer = dateAndIdLine(input.date, input.id);
    if (footer) {
        lines.push(footer);
    }
    return lines;
}
/**
 * Legacy line 2: `{role} {didAction}` - "Participant accepts with password
 * credentials.".
 *
 * Both parts arrive already localized; this only fixes the order and the
 * separator, so the three renderers cannot phrase the same attestation three
 * ways. A missing part is dropped rather than substituted - unlike the reason,
 * neither part blocks signing, and a record showing the stored token is
 * truthful where a lookup key is not.
 */
function composeSignatureAttestation(roleLabel, action) {
    const parts = [roleLabel, action].filter((part) => Boolean(part && part.trim()));
    return parts.length ? parts.join(' ') : undefined;
}
//# sourceMappingURL=signature_manifestation.js.map